import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';

export interface CreateAuthenticationTokenRequest {
    userId: string;
    userAgent: string;
}

export interface CreateAuthenticationTokenResponse {
    token: string;
    expiresAt: string;
}

export interface RotateAuthenticationTokenRequest {
    token: string;
}

export interface RotateAuthenticationTokenResponse {
    token: string
    expiresAt?: string;
    success: boolean;
    message?: string;
}

interface GoogleLoginResponse {
    token: string;
    email: string; 
}

@Injectable({
    providedIn: 'root'
})
export class AuthenticationTokenService {

    private readonly apiUrl = `${environment.url}/api`;

    constructor(private http: HttpClient) { }

    // Called upon successful credential login with Remember Me checked
    // Requests for a cookie token to be attached to the account and stores it in the browser cookie
    async createToken(request: CreateAuthenticationTokenRequest): Promise<boolean> {
        try {
            await firstValueFrom(this.http.post(
                `${this.apiUrl}/AuthenticationToken/CreateToken`,
                request,
                { withCredentials: true }
            ));

            console.log('[Token Service] Token created');

            return true;
        }
        catch (error: any) {
            console.error('[Token Service] Failed to create token:', error);

            if (error.status) {
                console.error('[Token Service] HTTP Status:', error.status);
            }
            return false;
        }
    }

    // Uses existing cookie to login
    // Old token is replaced with a new one and saved in the same browser cookie
    async rotateToken(): Promise<RotateAuthenticationTokenResponse | null> {
        try {
            const response = await firstValueFrom(this.http.post<RotateAuthenticationTokenResponse>(
                `${this.apiUrl}/AuthenticationToken/RotateToken`,
                {},
                { withCredentials: true }
            ));

            return response;
        }
        catch (error: any) {
            console.error('[Token Service] Failed to rotate token:', error);

            if (error.status) {
                console.error('[Token Service] HTTP Status:', error.status);
            }
            return null;
        }
    }

    // Revokes a given token
    async revokeToken(): Promise<void> {
        try {
            const response: any = await firstValueFrom(this.http.post(
                `${this.apiUrl}/AuthenticationToken/RevokeToken`,
                {},
                { withCredentials: true }
            ));

            console.log('[Token Service]', response?.message ?? 'Token revoked');

            return;
        }
        catch (error: any) {
            console.error('[Token Service] Failed to revoke token:', error);

            if (error.status) {
                console.error('[Token Service] HTTP Status:', error.status);
            }

            return;
        }
    }

    // Revokes all of a user's tokens
    async revokeAllTokens(userId: string): Promise<boolean> {
        try {
            await firstValueFrom(this.http.post(
                `${this.apiUrl}/AuthenticationToken/RevokeAllTokens`,
                { userId: userId }
            ));

            console.log('[Token Service ] All of users tokens revoked');

            return true
        }
        catch (error: any) {
            console.error('[Token Service] Failed to revoke all of users tokens:', error);

            if (error.status) {
                console.error('[Token Service] HTTP Status:', error.status);
            }

            return false;
        }
    }

    // Verifies user identity through Google, then checks with API on user account status
    async handleGoogleResponse(response: any): Promise<GoogleLoginResponse | null> {

        try {
            const result = await firstValueFrom(this.http.post<GoogleLoginResponse>(
                `${this.apiUrl}/AuthenticationToken/GoogleLogin`,
                { token: response.credential }
            ));

            console.log('[Google Login] Account verified');
            return result;
        }
        catch (error: any) {
            console.error('[Google Login] Failed:', error);
            return null;
        }
    }
}